import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Button,
  Badge,
  Input,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Box24Regular,
  Location24Regular,
  Calendar24Regular,
  ArrowRight16Regular,
} from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../../components/DataTable';
import { PortalOrder } from '../types';
import { categoryLabelForName } from '../productLabels';
import { fetchOrders, syncOrders } from '../services/portalApi';
import { usePortalDebug } from '../contexts/PortalDebugContext';
import { formatCalendarDate } from '../../lib/businessTime';

const CopyableUuid: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <span
      onClick={(e) => { e.stopPropagation(); copy(); }}
      title="Click to copy"
      style={{ fontSize: '11px', fontFamily: 'monospace', color: copied ? '#107c10' : '#a19f9d', cursor: 'pointer' }}
    >
      {label}: {copied ? 'copied!' : value}
    </span>
  );
};

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  searchBox: {
    width: '300px',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    ...shorthands.overflow('hidden'),
  },
  section: {
    marginBottom: '24px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('16px', '20px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  orderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  orderInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  orderNumber: {
    fontWeight: '600',
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  orderProduct: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  orderCategory: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  locationCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  locationText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  dateRange: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center' as const,
    ...shorthands.padding('48px'),
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('48px'),
  },
});

const mapOrder = (o: any): PortalOrder => ({
  ...o,
  orderNumber: o.number,
  startDate: o.startsAt,
  endDate: o.stopsAt,
  product: { id: '', name: o.productName || 'Dumpster Rental' },
  location: { address1: o.deliveryAddress || '—', city: '' },
  pricing: {
    basePrice: 0,
    deliveryFee: 0,
    pickupFee: 0,
    tax: 0,
    total: o.totalCents ? o.totalCents / 100 : 0,
    paid: o.paidCents ? o.paidCents / 100 : 0,
    balance: o.totalCents && o.paidCents ? (o.totalCents - o.paidCents) / 100 : 0,
  },
});

const Orders: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { debugMode } = usePortalDebug();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [orders, setOrders] = useState<PortalOrder[]>([]);

  useEffect(() => {
    const loadOrders = async () => {
      setLoading(true);
      try {
        const data = await fetchOrders();
        setOrders((data.orders || []).map(mapOrder));
      } catch (err) {
        console.error('Failed to load orders:', err);
      } finally {
        setLoading(false);
      }
    };
    loadOrders();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncOrders();
      const data = await fetchOrders();
      setOrders((data.orders || []).map(mapOrder));
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_transit':
        return <Badge appearance="filled" color="brand">In Transit</Badge>;
      case 'on_site':
        return <Badge appearance="filled" color="success">On Site</Badge>;
      case 'scheduled':
        return <Badge appearance="filled" color="warning">Scheduled</Badge>;
      case 'completed':
        return <Badge appearance="outline" color="success">Completed</Badge>;
      case 'cancelled':
        return <Badge appearance="outline" color="danger">Cancelled</Badge>;
      default:
        return <Badge appearance="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => formatCalendarDate(dateString);

  const filteredOrders = orders.filter(order => {
    return true;
  });

  // Separate active and completed orders
  const activeOrders = filteredOrders.filter(o => ['in_transit', 'on_site', 'scheduled'].includes(o.status));
  const completedOrders = filteredOrders.filter(o => ['completed', 'cancelled'].includes(o.status));

  const columns: ColumnConfig<PortalOrder>[] = [
    {
      columnKey: 'orderNumber',
      label: 'Order',
      width: '250px',
      render: (order) => (
        <div className={styles.orderInfo}>
          <Text
            className={styles.orderNumber}
            onClick={() => navigate(`/portal/orders/${order.id}`)}
          >
            #{order.orderNumber}
          </Text>
          <Text className={styles.orderProduct}>{order.product.name}</Text>
          {categoryLabelForName(order.product.name) && (
            <Text className={styles.orderCategory}>
              {categoryLabelForName(order.product.name)}
            </Text>
          )}
          {debugMode && (
            <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <CopyableUuid label="id" value={order.id} />
              {(order as any).booqable_id && (
                <CopyableUuid label="booqable" value={(order as any).booqable_id} />
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      columnKey: 'status',
      label: 'Status',
      width: '120px',
      render: (order) => getStatusBadge(order.status),
      groupValueFormatter: (value) => ({
        in_transit: 'In Transit',
        on_site: 'On Site',
        scheduled: 'Scheduled',
        completed: 'Completed',
      }[value] || value),
    },
    {
      columnKey: 'booqableStatus',
      label: 'Location',
      width: '250px',
      render: (order) => (
        <div className={styles.locationCell}>
          <Location24Regular style={{ color: tokens.colorNeutralForeground3 }} />
          <Text className={styles.locationText}>
            {order.location.address1}, {order.location.city}
          </Text>
        </div>
      ),
    },
    {
      columnKey: 'startDate',
      label: 'Date Range',
      width: '180px',
      render: (order) => (
        <div className={styles.dateRange}>
          <Calendar24Regular style={{ color: tokens.colorNeutralForeground3 }} />
          {formatDate(order.startDate)} → {formatDate(order.endDate)}
        </div>
      ),
    },
    {
      columnKey: 'id',
      label: '',
      width: '150px',
      render: (order) => (
        <div className={styles.actions}>
          {order.tracking?.status === 'en_route' ? (
            <Button
              appearance="primary"
              size="small"
              onClick={() => navigate(`/portal/tracking/${order.id}`)}
            >
              Track
            </Button>
          ) : order.status === 'on_site' ? (
            <Button appearance="outline" size="small">
              Request Pickup
            </Button>
          ) : (
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowRight16Regular />}
              iconPosition="after"
              onClick={() => navigate(`/portal/orders/${order.id}`)}
            >
              Details
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading orders..." />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className={styles.header}>
        <Text as="h1" className={styles.title}>Orders</Text>
        <Button
          appearance="subtle"
          icon={<ArrowRight16Regular />}
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing...' : 'Sync from Booqable'}
        </Button>
      </div>


      {filteredOrders.length > 0 ? (
        <div className={styles.card}>
          <DataTable
            data={filteredOrders}
            columns={columns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="portal_orders"
            onRowClick={(order) => navigate(`/portal/orders/${order.id}`)}
            itemsPerPage={10}
          />
        </div>
      ) : (
        <div className={styles.card}>
          <div className={styles.emptyState}>
            <Box24Regular style={{ fontSize: '48px', marginBottom: '16px' }} />
            <Text>
              {'You haven\'t placed any orders yet'}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
